import { Category, IImageThumbnail, IOrderRow } from '../../../Entities';
import {
    ImageEditorPhotoStatus,
    OrderRowQuantityInfo
} from '../../entry/queries/use-query-photo-status';

export type DeliveryProgress = {
    delivered: number;
    ordered: number;
};

export function getAdditionalProducts(photoStatus: ImageEditorPhotoStatus): OrderRowQuantityInfo[] {
    let quantities = photoStatus.orderRowQuantitiesInfo || [];

    return quantities.filter((quantity) => quantity.isUploadable === 0);
}

function isMarkedForDelivery(thumbnail: IImageThumbnail): boolean {
    if (thumbnail.overrideProperties) {
        return thumbnail.overrideProperties.categoryId === Category.NORMAL;
    }

    return thumbnail.categoryId === Category.NORMAL;
}

export function getDeliveryProgress(
    orderRow: IOrderRow,
    photoStatus: ImageEditorPhotoStatus,
    additionalProducts: OrderRowQuantityInfo[]
): DeliveryProgress | null {
    if (photoStatus.hasEditCustomerImages || photoStatus.hasFlex) {
        return null;
    }

    let photosInRow = (photoStatus.photosInRows || []).find(
        (row) => row.orderRowId === orderRow.id
    );

    if (!photosInRow) {
        return null;
    }

    let additionalOrdered = additionalProducts.reduce(
        (sum, product) => sum + product.expectedQuantity,
        0
    );

    return {
        delivered: orderRow.thumbnails.filter(isMarkedForDelivery).length,
        ordered: photosInRow.numberOfWantedPhotos + additionalOrdered
    };
}

export type ImageInProgress = {
    filename: string | null;
};

export type PendingUploads = {
    filenames: string[];
    uploadedAt: number;
};

export function countNormalImages(orderRow: IOrderRow): number {
    return orderRow.thumbnails.filter((thumbnail) => thumbnail.categoryId === Category.NORMAL)
        .length;
}

function normalizeFilename(filename: string): string {
    return filename.trim().toLowerCase();
}

function getListedFilenames(orderRow: IOrderRow): Set<string> {
    let uploads: UploadInProgress[] = orderRow.uploadsInProgress || [];

    return new Set(
        orderRow.thumbnails
            .map((thumbnail) => normalizeFilename(thumbnail.filename))
            .concat(uploads.map((upload) => normalizeFilename(upload.filename)))
    );
}

export function getPendingSkeletonCount(
    pending: PendingUploads | null,
    orderRow: IOrderRow,
    orderRowsUpdatedAt: number
): number {
    if (!pending) {
        return 0;
    }

    let serverHasSettled =
        orderRowsUpdatedAt > pending.uploadedAt && orderRow.editedPhotosUploadInProgress === false;

    if (serverHasSettled) {
        return 0;
    }

    let listed = getListedFilenames(orderRow);

    return pending.filenames.filter((filename) => !listed.has(normalizeFilename(filename))).length;
}

export function removePendingUploads(
    pending: PendingUploads | null,
    deletedFilenames: string[]
): PendingUploads | null {
    if (!pending) {
        return null;
    }

    let deleted = new Set(deletedFilenames.map(normalizeFilename));
    let filenames = pending.filenames.filter(
        (filename) => !deleted.has(normalizeFilename(filename))
    );

    return filenames.length === 0 ? null : { ...pending, filenames };
}

type UploadInProgress = {
    filename: string;
};

export function getImagesInProgress(
    orderRow: IOrderRow,
    photoStatus: ImageEditorPhotoStatus,
    includeScalingImages: boolean
): ImageInProgress[] {
    let uploads: UploadInProgress[] = orderRow.uploadsInProgress || [];
    let inProgress: ImageInProgress[] = uploads.map((upload) => ({ filename: upload.filename }));

    if (!includeScalingImages) {
        return inProgress;
    }

    let photosInRow = (photoStatus.photosInRows || []).find(
        (row) => row.orderRowId === orderRow.id
    );
    let scalingCount = photosInRow
        ? photosInRow.numberOfAddedPhotos - countNormalImages(orderRow) - inProgress.length
        : 0;

    for (let index = 0; index < scalingCount; index += 1) {
        inProgress.push({ filename: null });
    }

    return inProgress;
}
